import React, { useState, useEffect } from 'react';
import {
  Card, Button, Table, Tag, Space, Modal, Form, Input, message,
  Tabs, Statistic, Row, Col, Typography, Empty, Spin, Avatar, Divider, Timeline, Descriptions
} from 'antd';
import {
  CheckOutlined, CloseOutlined, EyeOutlined, CloudUploadOutlined,
  HistoryOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import api from '../api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export default function MiniProgramReviewPage() {
  const [loading, setLoading] = useState(true);
  const [pendingList, setPendingList] = useState([]);
  const [reviewHistory, setReviewHistory] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approvedToday: 0, rejectedToday: 0, totalOnline: 0, totalOffline: 0 });
  const [activeTab, setActiveTab] = useState('pending');
  const [showDetail, setShowDetail] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [currentMiniProgram, setCurrentMiniProgram] = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewModal, setPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [rejectForm] = Form.useForm();

  useEffect(() => {
    loadStats();
    loadPendingList();
  }, []);

  const loadStats = async () => {
    try {
      const res = await api.get('/admin/mini-review/stats');
      setStats(res);
    } catch (e) {
      console.error(e);
    }
  };

  const loadPendingList = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/mini-review/pending');
      setPendingList(res.list || []);
    } catch (e) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  const loadReviewHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/mini-review/history');
      setReviewHistory(res.list || []);
    } catch (e) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (versionId) => {
    try {
      const res = await api.get(`/admin/mini-review/detail/${versionId}`);
      setCurrentVersion(res.version);
      setCurrentMiniProgram(res.miniProgram);
      setAuditHistory(res.auditHistory || []);
      setShowDetail(true);
    } catch (e) {
      message.error('加载详情失败');
    }
  };

  // 审核通过
  const handleApprove = async (versionId, comment = '') => {
    try {
      await api.post(`/admin/mini-review/approve/${versionId}`, { comment });
      message.success('审核通过');
      loadPendingList();
      loadStats();
    } catch (e) {
      message.error(e.error || '操作失败');
    }
  };

  // 审核驳回
  const handleReject = async (versionId, reason) => {
    if (!reason) {
      message.error('请填写驳回原因');
      return;
    }
    try {
      await api.post(`/admin/mini-review/reject/${versionId}`, { reason });
      message.success('已驳回');
      rejectForm.resetFields();
      loadPendingList();
      loadStats();
    } catch (e) {
      message.error(e.error || '操作失败');
    }
  };

  // 发布上线
  const handlePublish = async (versionId) => {
    try {
      await api.post(`/admin/mini-review/publish/${versionId}`);
      message.success('发布成功');
      loadPendingList();
      loadStats();
    } catch (e) {
      message.error(e.error || '发布失败');
    }
  };

  // 强制下架
  const handleOffline = async (id, reason) => {
    try {
      await api.post(`/admin/mini-review/offline/${id}`, { reason });
      message.success('已下架');
      loadPendingList();
      loadStats();
    } catch (e) {
      message.error(e.error || '操作失败');
    }
  };

  // 预览/运行小程序
  const handlePreview = async (versionId) => {
    try {
      setPreviewLoading(true);
      setPreviewModal(true);
      const data = await api.get(`/admin/mini-review/preview/${versionId}`);
      if (data.previewUrl) setPreviewUrl(data.previewUrl);
      else message.error('无法生成预览地址: ' + (data.format || ''));
    } catch (e) {
      message.error(e.error || '预览失败');
      setPreviewModal(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // 切换标签
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'pending') loadPendingList();
    else if (key === 'history') loadReviewHistory();
  };

  const pendingColumns = [
    {
      title: '小程序',
      render: (_, record) => (
        <Space>
          <Avatar shape="square" size={40} src={record.icon_url} />
          <Space direction="vertical" size={0}>
            <Text strong>{record.mp_name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.app_id}</Text>
          </Space>
        </Space>
      ),
    },
    {
      title: '版本号',
      dataIndex: 'version',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text>v{text}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.type === 'experience' ? '体验版' : '正式版'}
          </Text>
        </Space>
      ),
    },
    {
      title: '提交人',
      dataIndex: 'developer_name',
      render: (text) => text || '管理员',
    },
    {
      title: '提交时间',
      dataIndex: 'submit_audit_time',
      render: (text) => text ? new Date(text).toLocaleString() : '--',
    },
    {
      title: '操作',
      render: (_, record) => (
        <Space>
          <Button size="small" type="primary" icon={<EyeOutlined />} onClick={() => loadDetail(record.id)}>
            审核
          </Button>
        </Space>
      ),
    },
  ];

  const historyColumns = [
    {
      title: '小程序',
      dataIndex: 'mp_name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>v{record.version || '--'}</Text>
        </Space>
      ),
    },
    {
      title: '操作类型',
      dataIndex: 'audit_type',
      render: (type) => {
        const map = {
          approve: { color: 'green', text: '审核通过' },
          reject: { color: 'red', text: '驳回' },
          publish: { color: 'blue', text: '发布上线' },
          offline: { color: 'orange', text: '强制下架' },
        };
        const m = map[type] || { color: 'default', text: type };
        return <Tag color={m.color}>{m.text}</Tag>;
      },
    },
    {
      title: '审核人',
      dataIndex: 'reviewer_name',
      render: (text) => text || '--',
    },
    {
      title: '审核意见',
      dataIndex: 'comment',
      render: (text, record) => text || record.reject_reason || '--',
    },
    {
      title: '时间',
      dataIndex: 'audit_time',
      render: (text) => text ? new Date(text).toLocaleString() : '--',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={4}>小程序审核管理</Title>

      {/* 统计 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card><Statistic title="待审核" value={stats.pendingCount} valueStyle={{ color: '#faad14' }} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="今日通过" value={stats.approvedToday} valueStyle={{ color: '#3f8600' }} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="今日驳回" value={stats.rejectedToday} valueStyle={{ color: '#cf1322' }} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="已上线" value={stats.totalOnline} valueStyle={{ color: '#1890ff' }} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="已下架" value={stats.totalOffline} valueStyle={{ color: '#8c8c8c' }} /></Card>
        </Col>
      </Row>

      {/* 标签页 */}
      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <Tabs.TabPane tab={`待审核 (${stats.pendingCount})`} key="pending" />
        <Tabs.TabPane tab="审核历史" key="history" />
      </Tabs>

      {/* 列表 */}
      <Card>
        {activeTab === 'pending' ? (
          <Table
            columns={pendingColumns}
            dataSource={pendingList}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: <Empty description="暂无待审核的小程序" /> }}
          />
        ) : (
          <Table
            columns={historyColumns}
            dataSource={reviewHistory}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: <Empty description="暂无审核历史" /> }}
          />
        )}
      </Card>

      {/* 审核详情弹窗 */}
      <Modal
        title="审核详情"
        open={showDetail}
        onCancel={() => setShowDetail(false)}
        footer={null}
        width={700}
      >
        {currentMiniProgram && currentVersion && (
          <>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="小程序名称">{currentMiniProgram.name}</Descriptions.Item>
              <Descriptions.Item label="AppId">{currentMiniProgram.app_id}</Descriptions.Item>
              <Descriptions.Item label="版本号">v{currentVersion.version}</Descriptions.Item>
              <Descriptions.Item label="版本类型">{currentVersion.type === 'experience' ? '体验版' : '正式版'}</Descriptions.Item>
              <Descriptions.Item label="描述" span={2}>{currentMiniProgram.description || '--'}</Descriptions.Item>
              <Descriptions.Item label="代码包地址">
                <Text copyable ellipsis style={{ maxWidth: 300 }}>{currentVersion.code_url || '--'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="MD5">{currentVersion.package_md5 || '--'}</Descriptions.Item>
              <Descriptions.Item label="更新时间">{currentVersion.updated_at}</Descriptions.Item>
            </Descriptions>

            {/* 审核历史时间线 */}
            {auditHistory.length > 0 && (
              <>
                <Divider orientation="left">审核记录</Divider>
                <Timeline
                  items={auditHistory.map(a => ({
                    color: a.status === 'approved' ? 'green' : a.status === 'rejected' ? 'red' : 'blue',
                    children: (
                      <Space direction="vertical" size={0}>
                        <Text strong>{a.audit_type === 'submit' ? '提交审核' : a.audit_type === 'approve' ? '审核通过' : a.audit_type === 'reject' ? '驳回' : a.audit_type}</Text>
                        <Text type="secondary">{a.comment || a.reject_reason || ''}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>{a.created_at}</Text>
                      </Space>
                    ),
                  }))}
                />
              </>
            )}

            {/* 操作按钮（仅审核中状态显示） */}
            {currentVersion.status === 'auditing' && (
              <div style={{ marginTop: 24, textAlign: 'right' }}>
                <Space>
                  <Button icon={<EyeOutlined />} onClick={() => handlePreview(currentVersion.id)}>
                    预览/运行
                  </Button>
                  <Modal
                    title="驳回确认"
                    open={false}
                    footer={null}
                  >
                    <Form form={rejectForm} onFinish={(values) => handleReject(currentVersion.id, values.reason)}>
                      <Form.Item name="reason" label="驳回原因" rules={[{ required: true, message: '请填写驳回原因' }]}>
                        <TextArea rows={3} placeholder="请详细说明驳回原因" />
                      </Form.Item>
                      <Button type="primary" danger htmlType="submit">确认驳回</Button>
                    </Form>
                  </Modal>
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    onClick={() => {
                      const reason = window.prompt('请输入驳回原因：');
                      if (reason) handleReject(currentVersion.id, reason);
                    }}
                  >
                    驳回
                  </Button>
                  <Button type="primary" icon={<CheckOutlined />} onClick={() => handleApprove(currentVersion.id)}>
                    通过
                  </Button>
                </Space>
              </div>
            )}

            {/* 审核通过后显示发布按钮 */}
            {currentVersion.status === 'approved' && (
              <div style={{ marginTop: 24, textAlign: 'right' }}>
                <Space>
                  <Button icon={<EyeOutlined />} onClick={() => handlePreview(currentVersion.id)}>
                    预览/运行
                  </Button>
                  <Button type="primary" icon={<CloudUploadOutlined />} onClick={() => { handlePublish(currentVersion.id); setShowDetail(false); }}>
                    发布上线
                  </Button>
                </Space>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* 预览/运行小程序 */}
      <Modal
        title={<span><EyeOutlined /> 预览/运行小程序</span>}
        open={previewModal}
        onCancel={() => { setPreviewModal(false); setPreviewUrl(''); }}
        footer={null}
        width="92%"
        style={{ maxWidth: 480, top: 24 }}
        styles={{ body: { height: '80vh', padding: 0 } }}
      >
        {previewLoading ? (
          <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin tip="正在准备预览..." />
          </div>
        ) : previewUrl ? (
          <iframe
            src={previewUrl}
            title="mini-program-preview"
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        ) : (
          <div style={{ padding: 24 }}>暂无预览</div>
        )}
      </Modal>
    </div>
  );
}
